import { HelpIcon } from "@dehub/theme"
import { Anchor, Box, Grid, Group, Stack, Text } from "@mantine/core"
import { FileTextIcon, MapPinIcon, PhoneIcon } from "@phosphor-icons/react"
import { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { formatReceiverAddress } from "../../utils/format-branch-address"
import { renderBusinessName } from "../../utils/render-business-name"
import { ChannelPill } from "./dn-detail-header"
import { DnDetail, formatDateTime, formatMoney } from "./dn-detail-utils"
import classes from "./dn-detail.module.css"

export function DnSectionTitle({ icon, title, right, helpKey }: Readonly<{ icon?: ReactNode; title: string; right?: ReactNode; helpKey?: string }>) {
  return (
    <Group justify="space-between" align="center">
      <Group gap="sm" align="center" wrap="nowrap">
        {icon && <Box className={classes.sectionIcon}>{icon}</Box>}
        <Text className={classes.sectionTitle}>{title}{helpKey && <HelpIcon helpKey={helpKey} variant="section" />}</Text>
      </Group>
      {right}
    </Group>
  )
}

function InfoItem({ label, children, span = 6 }: Readonly<{ label: string; children: ReactNode; span?: number }>) {
  return (
    <Grid.Col span={{ base: 12, sm: span }}>
      <Stack gap={2}>
        <Text className={classes.label}>{label}</Text>
        <Box>{children}</Box>
      </Stack>
    </Grid.Col>
  )
}

export const DnDivider = () => <Box className={classes.divider} />

/** «Гол» tab, upper part: delivery address (buyer, phone, branch address) and order facts. */
export function DnOverview({ dn }: Readonly<{ dn: DnDetail }>) {
  const { t } = useTranslation("delivery")
  // O84/O85: GET /delivery_note/:id returns the BUYER and its branch on the DN itself; no order.* fallback.
  const address = formatReceiverAddress(dn.receiverBranch, dn.receiverWarehouse)
  const deliverAt = dn.rescheduledDeliveryDate || dn.deliveryDate
  const channel = dn.classification?.deliveryChannel ?? null
  const paid = dn.paidAmount ?? 0
  const due = dn.amountToPay ?? 0

  return (
    <Stack gap="md">
      <DnSectionTitle icon={<MapPinIcon size={18} />} title={t("dnDetail.deliveryAddress")} helpKey="order.supplier-delivery-detail.section.address" />
      <Grid gap="md">
        <InfoItem label={t("dnDetail.receiver")}>
          <Text className={classes.value}>{renderBusinessName(dn.receiverBusiness, "-")}</Text>
        </InfoItem>
        <InfoItem label={t("dnDetail.contactPhone")}>
          {dn.receiverPhone ? (
            <Anchor href={`tel:${dn.receiverPhone}`} className={classes.mono} c="var(--nw-text)" translate="no">
              {dn.receiverPhone}
            </Anchor>
          ) : (
            <Text className={classes.value}>-</Text>
          )}
        </InfoItem>
        <InfoItem label={t("dnDetail.address")} span={12}>
          <Text className={classes.value}>{address ?? t("dnDetail.addressNotSet")}</Text>
        </InfoItem>
        <InfoItem label={t("dnDetail.deliveryTime")}>
          <Text className={classes.value}>{formatDateTime(deliverAt)}</Text>
          {dn.rescheduledDeliveryDate && dn.deliveryDate && (
            <Text className={classes.hint}>{t("dnDetail.postponedOriginal", { date: formatDateTime(dn.deliveryDate) })}</Text>
          )}
        </InfoItem>
        <InfoItem label={t("dnDetail.receiptVerification")}>
          <Group gap={6} wrap="nowrap">
            <PhoneIcon size={16} />
            <Text className={classes.value}>{t("dnDetail.smsOtpRequired")}</Text>
          </Group>
        </InfoItem>
      </Grid>

      <DnDivider />

      <DnSectionTitle icon={<FileTextIcon size={18} />} title={t("dnDetail.orderInfo")} helpKey="order.supplier-delivery-detail.section.order-info" />
      <Grid gap="md">
        <InfoItem label={t("dnDetail.salesOrder")} span={4}>
          {dn.salesCode && dn.orderId ? (
            <Anchor component={Link} to={`/supplier-sales/detail/${dn.orderId}`} className={`${classes.mono} ${classes.link}`} translate="no">
              {dn.salesCode}
            </Anchor>
          ) : (
            <Text className={classes.value}>{dn.salesCode ?? "-"}</Text>
          )}
        </InfoItem>
        <InfoItem label={t("dnDetail.deliveryType")} span={4}>
          {channel ? <ChannelPill channel={channel} /> : <Text className={classes.value}>-</Text>}
        </InfoItem>
        <InfoItem label={t("dnDetail.orderCreated")} span={4}>
          <Text className={classes.mono}>{formatDateTime(dn.createdAt)}</Text>
        </InfoItem>
        <InfoItem label={t("dnDetail.orderAmount")} span={4}>
          <Text className={classes.mono} fw={700}>
            {formatMoney(dn.totalAmount ?? 0)}
          </Text>
        </InfoItem>
        <InfoItem label={t("dnDetail.advancePayment")} span={4}>
          <Text className={paid > 0 ? classes.mono : classes.value}>{paid > 0 ? formatMoney(paid) : t("dnDetail.notPaid")}</Text>
        </InfoItem>
        <InfoItem label={t("dnDetail.balanceCod")} span={4}>
          <Text className={due > 0 ? classes.mono : classes.value} fw={700}>
            {due > 0 ? formatMoney(due) : t("dnDetail.noCharge")}
          </Text>
        </InfoItem>
      </Grid>
    </Stack>
  )
}
